import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  memo,
} from 'react';
import {
  extendControlRender,
  extendsRender,
  headers,
  renderAction,
  renderCategory,
  renderLimit,
  renderStatus,
} from './constants';
import { Maybe } from '@apolloGenerated';
import { bem } from 'classnames-bem';
import { isArray } from '@apollo/client/utilities';
import { DateTime } from 'luxon';
import sdk from '@aml/sdk';
import { StatusField } from '@aml/sdk/dashboard';
import {
  SortType,
  TableController,
  Spacer,
  LoaderOverlay,
  Informer,
} from '@rubin-dev/goblin';
import styles from './transactions.module.scss';
import { renderCellDate, renderCellHash } from '@shared/ui';
import { AppContext } from '../../contexts';
import { DashboardFilters } from './TransactionsScreen';
import { DashboardOrder } from './types';
import { useTranslation } from 'react-i18next';
import { HeaderControls } from './HeaderControls';

type OverLimitProps = {
  updateTotal: () => void;
  initFilters: DashboardFilters;
};
const pageSize = 3;

const MemoizedHeaderControls = memo(HeaderControls);
const MemoizedTableControls = memo(TableController);

export const OverLimitTable: FC<PropsWithChildren<OverLimitProps>> = ({
  children,
  updateTotal,
  initFilters: { hash: initHash, dateFrom: initDateFrom, dateTo: initDateTo },
}) => {
  const { t } = useTranslation();
  const { language } = useContext(AppContext);
  const [page, setPage] = useState(1);

  const [orderBy, setSortBy] = useState<DashboardOrder | null>(DashboardOrder.Asc);

  const [date, setDate] = useState<DateTime[]>(
    initDateFrom && initDateTo
      ? [DateTime.fromISO(initDateFrom), DateTime.fromISO(initDateTo)]
      : [],
  );
  const [hash, setHash] = useState<string>(initHash || '');
  const [loading, setLoading] = useState<boolean>(false);

  const [extendRow, setExtendRow] = useState<Maybe<number>>(null);
  const [params, setParams] = useState<{
    hash?: string;
    page?: number;
    pageSize?: number;
    from?: string;
    to?: string;
    status?: 'CLEAN' | 'BLOCKED' | 'OVER_LIMIT';
    orderBy?: DashboardOrder;
  }>({
    hash: initHash || '',
    page: 1,
    pageSize,
    from: initDateFrom,
    to: initDateTo,
    status: 'OVER_LIMIT' as const,
    orderBy: DashboardOrder.Asc,
  });

  const { data, isLoading } = sdk.dashboard.queries.useGetTransactionList(
    params.hash,
    params.page,
    params.pageSize,
    params.from,
    params.to,
    params.status,
    params.orderBy,
    {
      enabled: !!params.status,
    },
  );

  const { mutateAsync } = sdk.dashboard.mutations.usePostSetStatusToRiskTransaction();

  const memoizedHeaders = useMemo(headers, []);

  useEffect(() => {
    setParams({
      hash,
      page,
      pageSize,
      from: isArray(date) && date?.length > 1 ? date[0].toISODate() : undefined,
      to: isArray(date) && date?.length > 1 ? date[1].toISODate() : undefined,
      status: 'OVER_LIMIT',
      orderBy: orderBy || undefined,
    });
  }, [hash, page, date, orderBy]);

  const changeStatus = useCallback(async (id: number, status: StatusField) => {
    setLoading(true);

    await mutateAsync({
      id,
      status,
    });

    setPage(1);

    if (updateTotal) updateTotal();
    setLoading(false);
  }, []);

  const edge = useMemo(
    () =>
      data?.transactionList?.map((item) => ({
        hash: renderCellHash({
          hash: item.tx_id || '',
          network: item.network,
          type: 'transaction',
          once: true,
        }),
        date: renderCellDate({ timestamp: item?.updated_at || 0 }),
        token: item.network.toUpperCase(),
        category: renderCategory(item.riskCategories),
        receivingLimit: renderLimit(item.riskCategories),
        status: renderStatus(item.status),
        actions: renderAction(changeStatus, item.id),
        extendsRender: extendRow === item.id ? extendsRender(item) : '',
        extendControl: extendControlRender(extendRow === item.id, () =>
          extendRow ? setExtendRow(null) : setExtendRow(item.id),
        ),
      })) || [],
    [data, extendRow, language],
  );

  const handleSortChange = useCallback(
    (sort: string | null) =>
      setSortBy(
        sort === 'asc'
          ? DashboardOrder.Asc
          : sort === 'desc'
          ? DashboardOrder.Desc
          : null,
      ),
    [],
  );

  const handlePageChange = useCallback((page: number) => setPage(page), []);

  const handleHashChange = useCallback((hash: string) => {
    setHash(hash);
    setPage(1);
  }, []);

  const handleDateChange = useCallback((date: DateTime[]) => {
    setDate(date);
    setPage(1);
  }, []);

  return (
    <>
      <div className={bem('controls')}>
        <MemoizedHeaderControls
          date={date}
          hash={hash}
          onChangeHash={handleHashChange}
          onChangeDate={handleDateChange}
          onConfirm={() => {}}
        />
        {children}
      </div>
      <div className={styles.tableWrapper}>
        <LoaderOverlay show={isLoading || loading} />
        <Spacer size={4} />
        {!edge.length ? (
          <div className={styles.information}>
            <Informer
              type="empty"
              title={t('strings.noresults')}
              subtitle={t('explorer.noResultFound')}
            />
          </div>
        ) : (
          <MemoizedTableControls
            data={edge}
            headers={memoizedHeaders}
            total={data?.total || 0}
            initPage={page}
            initSort={orderBy?.toLowerCase() as SortType}
            initSortBy="date"
            onSort={handleSortChange}
            onChangePage={handlePageChange}
            pageSize={pageSize}
          />
        )}
      </div>
    </>
  );
};
